import { cn, RankBadge, RAW_RANKS, type RawRank } from "@tcg/ui";
import { useId } from "react";
import { useTranslation } from "react-i18next";

/**
 * The shop's Rank for a raw card (R16 — A 美品 / B 軽い傷 / C 傷あり / D ダメージ): four big buttons with
 * the letter and its meaning. Required: until one is chosen the group is outlined red and says so.
 */
export function RankPicker({
  value,
  onChange,
  required = true,
  label,
}: {
  value: RawRank | null;
  onChange: (rank: RawRank) => void;
  required?: boolean;
  label?: string;
}) {
  const { t } = useTranslation();
  const missing = required && value === null;
  const messageId = useId();
  return (
    // (a fieldset takes no aria-required — the red outline + the sentence say it is needed)
    <fieldset
      aria-invalid={missing ? true : undefined}
      aria-describedby={missing ? messageId : undefined}
      className={cn(
        "flex flex-col gap-2 rounded-md border p-2",
        missing ? "border-2 border-status-problem" : "border-transparent",
      )}
    >
      <legend className="px-1 font-medium">{label ?? t("buylist.rank")}</legend>
      <div className="grid grid-cols-4 gap-2">
        {RAW_RANKS.map((rank) => (
          <button
            key={rank}
            type="button"
            aria-pressed={value === rank}
            aria-label={`${rank} — ${t(`rank.${rank}`)}`}
            onClick={() => onChange(rank)}
            className={cn(
              "flex min-h-pos-touch flex-col items-center justify-center gap-1 rounded-md border-2 px-2 py-1",
              value === rank ? "border-primary bg-accent" : "border-border",
            )}
          >
            <RankBadge rank={rank} size="lg" />
            <span className="text-sm">{t(`rank.${rank}`)}</span>
          </button>
        ))}
      </div>
      {missing ? (
        <p id={messageId} className="text-sm text-pos-problem">
          {t("buylist.rank_required")}
        </p>
      ) : null}
    </fieldset>
  );
}
